/**
 * Pure time-axis logic for composite loop mode. Each station keeps its own frame
 * cadence (workers/CONTRACTS.md: manifests carry the trailing 60 min, independently
 * per station) — there is no global frame index to align to, so composite scrubbing
 * works over a shared millisecond time axis and asks each station for its nearest
 * frame at-or-before that time.
 */
import type { HotManifest, ManifestFrame } from "./types";

/** Nearest frame at or before targetMs (by dataTimeLastMs). `frames` must be
 * oldest -> newest, per the manifest contract. Null if the station has no
 * coverage at that time (e.g. scrubbed earlier than its first known frame).
 * Generic over any frame shape carrying `dataTimeLastMs` (not just the base
 * `ManifestFrame`) so mosaicController.ts can call this directly against
 * `MosaicManifestFrame[]` (mosaicTypes.ts, which extends ManifestFrame with
 * `contributing`) and get the richer type back, not a widened one. */
export function frameAtOrBefore<T extends Pick<ManifestFrame, "dataTimeLastMs">>(frames: readonly T[], targetMs: number): T | null {
  let result: T | null = null;
  for (const frame of frames) {
    if (frame.dataTimeLastMs <= targetMs) result = frame;
    else break;
  }
  return result;
}

/** Scrub time axis spanning every active station's known frames (by dataTimeLastMs);
 * null until at least one station has reported a manifest with frames. */
export function globalTimeRange(manifests: ReadonlyMap<string, HotManifest | null>): { minMs: number; maxMs: number } | null {
  let minMs = Infinity;
  let maxMs = -Infinity;
  for (const manifest of manifests.values()) {
    if (!manifest || manifest.frames.length === 0) continue;
    const first = manifest.frames[0].dataTimeLastMs;
    const last = manifest.frames[manifest.frames.length - 1].dataTimeLastMs;
    if (first < minMs) minMs = first;
    if (last > maxMs) maxMs = last;
  }
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) return null;
  return { minMs, maxMs };
}

/** Searches outward from `startIdx` in `items` — radius 0 (the start index
 * itself), then ±1, ±2, ... up to `maxRadius` — returning the first item
 * `isUsable` accepts. The core "nearest loaded frame" selection strategy
 * behind mosaicController.ts's/mosaicGlController.ts's own
 * showNearestLoaded(): while dragging the scrubber, prefer the CLOSEST
 * already-cached frame to the drag target over just "whatever happens to
 * be cached anywhere," so the instant feedback stays spatially coherent
 * with where the user is actually scrubbing rather than jumping to a
 * distant cached frame. Pulled out as a pure, controller-independent
 * function so the search order itself is unit-testable without a real
 * MapLibre map/cache (both controllers need those for everything else,
 * which is why they otherwise have no direct unit tests — see
 * mosaicTimeline.ts's own doc comment on that established pattern).
 * Returns null if nothing within `maxRadius` satisfies `isUsable`. */
export function findNearestUsable<T>(items: readonly T[], startIdx: number, maxRadius: number, isUsable: (item: T) => boolean): T | null {
  for (let radius = 0; radius <= maxRadius; radius++) {
    const candidateIndices = radius === 0 ? [startIdx] : [startIdx - radius, startIdx + radius];
    for (const idx of candidateIndices) {
      const item = items[idx];
      if (item !== undefined && isUsable(item)) return item;
    }
  }
  return null;
}
